import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import API from "../../services/api.ts";

const SPECIALTIES = [
    { value: "radiology", label: "Radiology" },
    { value: "internal_medicine", label: "Internal Medicine" },
    { value: "emergency_medicine", label: "Emergency Medicine" },
    { value: "cardiology", label: "Cardiology" },
    { value: "ophthalmology", label: "Ophthalmology" },
    { value: "general_practice", label: "General Practice" },
    { value: "other", label: "Other" },
];

const TRAINING_LEVELS = [
    { value: "medical_student", label: "Medical student" },
    { value: "resident", label: "Resident" },
    { value: "fellow", label: "Fellow" },
    { value: "attending", label: "Attending" },
    { value: "other", label: "Other" },
];

const LLM_FREQUENCIES = [
    { value: "never", label: "Never" },
    { value: "rarely", label: "Rarely" },
    { value: "monthly", label: "Monthly" },
    { value: "weekly", label: "Weekly" },
    { value: "daily", label: "Daily" },
];

/**
 * Blocking modal shown the first time a reviewer enters a study.
 * Prefills demographic fields from /accounts/users/me/profile/ when available
 * and persists answers to both the per-study reader profile and the user's
 * shared profile (so the next study auto-prefills).
 */
const ReaderProfileModal = ({ studyId, onConfirmed, onCancel, onClose }) => {
    const [email, setEmail] = useState("");
    const [nameOrId, setNameOrId] = useState("");
    const [specialty, setSpecialty] = useState("");
    const [trainingLevel, setTrainingLevel] = useState("");
    const [yearsPractice, setYearsPractice] = useState("");
    const [priorLLMUse, setPriorLLMUse] = useState("");
    const [consent, setConsent] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const prefill = async () => {
            try {
                const [meRes, profileRes] = await Promise.all([
                    API.get("/accounts/users/me/"),
                    API.get("/accounts/users/me/profile/").catch(() => null),
                ]);
                if (cancelled) return;
                const me = meRes.data || {};
                setEmail(me.email || "");
                const fullName = [me.first_name, me.last_name].filter(Boolean).join(" ");
                setNameOrId(fullName || me.username || "");
                const p = profileRes?.data;
                if (p) {
                    setSpecialty(p.specialty || "");
                    setTrainingLevel(p.training_level || "");
                    setYearsPractice(
                        p.years_independent_practice === null || p.years_independent_practice === undefined
                            ? ""
                            : String(p.years_independent_practice)
                    );
                    setPriorLLMUse(p.prior_llm_use || "");
                }
            } catch (err) {
                console.error("Failed to prefill reader profile:", err);
            } finally {
                if (!cancelled) setLoaded(true);
            }
        };
        prefill();
        return () => {
            cancelled = true;
        };
    }, []);

    const canSubmit =
        consent &&
        email.trim() &&
        nameOrId.trim() &&
        specialty &&
        trainingLevel &&
        yearsPractice !== "" &&
        priorLLMUse &&
        !submitting;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        try {
            setSubmitting(true);
            const yearsNum = parseInt(yearsPractice, 10);
            await API.post(`/studies/${studyId}/reader_profile/`, {
                email: email.trim(),
                reader_name_or_id: nameOrId.trim(),
                specialty,
                training_level: trainingLevel,
                years_independent_practice: Number.isNaN(yearsNum) ? null : yearsNum,
                prior_llm_use: priorLLMUse,
                consent: true,
            });
            onConfirmed && onConfirmed();
        } catch (err) {
            console.error("Failed to save reader profile:", err);
            toast.error(
                err?.response?.data?.error ||
                    "Could not save your study profile. Please try again."
            );
        } finally {
            setSubmitting(false);
        }
    };

    const handleClose = onClose || onCancel;

    return (
        <div
            className="modal d-block reader-profile-modal"
            style={{
                backgroundColor: "rgba(5, 10, 30, 0.85)",
                backdropFilter: "blur(4px)",
            }}
        >
            <style>{`
                .reader-profile-modal .rpm-label {
                    color: #cbd5e1;
                    font-size: 0.85rem;
                    font-weight: 500;
                    margin-bottom: 0.35rem;
                    display: block;
                }
                .reader-profile-modal .rpm-input,
                .reader-profile-modal .rpm-select {
                    background-color: rgba(15, 23, 42, 0.55);
                    border: 1px solid rgba(148, 163, 184, 0.35);
                    color: #f1f5f9;
                    border-radius: 8px;
                    padding: 0.5rem 0.75rem;
                    width: 100%;
                }
                .reader-profile-modal .rpm-input::placeholder {
                    color: rgba(203, 213, 225, 0.55);
                }
                .reader-profile-modal .rpm-input:focus,
                .reader-profile-modal .rpm-select:focus {
                    outline: none;
                    border-color: #22d3ee;
                    box-shadow: 0 0 0 0.2rem rgba(34, 211, 238, 0.25);
                }
                .reader-profile-modal .rpm-select option {
                    background-color: #0f172a;
                    color: #e2e8f0;
                }
                .reader-profile-modal .modal-footer {
                    background: transparent;
                    border-top: none;
                }
                .reader-profile-modal .rpm-close {
                    position: absolute;
                    top: 14px;
                    right: 14px;
                    background: transparent;
                    border: none;
                    color: #94a3b8;
                    width: 32px;
                    height: 32px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    font-size: 1.25rem;
                    line-height: 1;
                    transition: background 0.15s, color 0.15s;
                    z-index: 2;
                }
                .reader-profile-modal .rpm-close:hover {
                    background: rgba(148, 163, 184, 0.18);
                    color: #f1f5f9;
                }
            `}</style>
            <div className="modal-dialog modal-lg modal-dialog-centered">
                <div
                    className="modal-content"
                    style={{
                        background: "linear-gradient(180deg, #0f172a 0%, #111c34 100%)",
                        color: "#e2e8f0",
                        border: "1px solid rgba(94, 234, 212, 0.2)",
                        borderRadius: "14px",
                        boxShadow: "0 20px 60px rgba(0, 0, 0, 0.6)",
                        position: "relative",
                    }}
                >
                    <button
                        type="button"
                        className="rpm-close"
                        aria-label="Close"
                        onClick={handleClose}
                        disabled={submitting}
                    >
                        <i className="fa fa-times"></i>
                    </button>
                    <form onSubmit={handleSubmit}>
                        <div className="modal-body p-4">
                            <div className="mb-3">
                                <div style={{ color: "#5eead4", fontSize: "0.85rem", fontWeight: 500 }}>
                                    Reader registration
                                </div>
                                <h3 className="fw-bold mb-2" style={{ color: "#fff" }}>
                                    Create your study profile
                                </h3>
                                <p className="small mb-0" style={{ color: "#94a3b8" }}>
                                    This information will be used to assign cases by specialty and
                                    to analyze diagnostic performance by training background and
                                    prior AI use.
                                </p>
                            </div>

                            {!loaded ? (
                                <div className="text-center py-4">
                                    <div className="spinner-border" role="status" style={{ color: "#5eead4" }}>
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : (
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="rpm-label">Email</label>
                                        <input
                                            type="email"
                                            className="rpm-input"
                                            placeholder="name@example.com"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="rpm-label">Name or study ID</label>
                                        <input
                                            type="text"
                                            className="rpm-input"
                                            placeholder="Dr. Smith or R001"
                                            value={nameOrId}
                                            onChange={(e) => setNameOrId(e.target.value)}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="rpm-label">Specialty</label>
                                        <select
                                            className="rpm-select"
                                            value={specialty}
                                            onChange={(e) => setSpecialty(e.target.value)}
                                            required
                                        >
                                            <option value="">Select specialty</option>
                                            {SPECIALTIES.map((o) => (
                                                <option key={o.value} value={o.value}>
                                                    {o.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="rpm-label">Training level</label>
                                        <select
                                            className="rpm-select"
                                            value={trainingLevel}
                                            onChange={(e) => setTrainingLevel(e.target.value)}
                                            required
                                        >
                                            <option value="">Select level</option>
                                            {TRAINING_LEVELS.map((o) => (
                                                <option key={o.value} value={o.value}>
                                                    {o.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="rpm-label">Years of independent practice after residency</label>
                                        <input
                                            type="number"
                                            min="0"
                                            className="rpm-input"
                                            placeholder="0"
                                            value={yearsPractice}
                                            onChange={(e) => setYearsPractice(e.target.value)}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="rpm-label">Prior LLM use</label>
                                        <select
                                            className="rpm-select"
                                            value={priorLLMUse}
                                            onChange={(e) => setPriorLLMUse(e.target.value)}
                                            required
                                        >
                                            <option value="">Select frequency</option>
                                            {LLM_FREQUENCIES.map((o) => (
                                                <option key={o.value} value={o.value}>
                                                    {o.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-12">
                                        <div
                                            className="p-3 d-flex align-items-start gap-2"
                                            style={{
                                                border: "1px solid rgba(148, 163, 184, 0.4)",
                                                borderRadius: "10px",
                                            }}
                                        >
                                            <input
                                                type="checkbox"
                                                id="reader-consent"
                                                className="form-check-input mt-1"
                                                checked={consent}
                                                onChange={(e) => setConsent(e.target.checked)}
                                            />
                                            <label
                                                htmlFor="reader-consent"
                                                className="small mb-0"
                                                style={{ cursor: "pointer", color: "#e2e8f0" }}
                                            >
                                                I consent to participate in this research study and understand
                                                that my diagnostic responses, timing, and AI interactions may
                                                be recorded for research analysis.
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer border-0 px-4 pb-4 pt-0">
                            <button
                                type="submit"
                                className="btn"
                                disabled={!canSubmit}
                                style={{
                                    background: canSubmit
                                        ? "linear-gradient(135deg, #67e8f9 0%, #22d3ee 100%)"
                                        : "rgba(103, 232, 249, 0.3)",
                                    color: "#0f172a",
                                    fontWeight: 600,
                                    border: "none",
                                    padding: "10px 20px",
                                    borderRadius: "8px",
                                }}
                            >
                                {submitting ? "Saving..." : "Continue to dashboard"}
                            </button>
                            <button
                                type="button"
                                className="btn btn-outline-light"
                                onClick={onCancel}
                                disabled={submitting}
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ReaderProfileModal;
